import { ref, type ComputedRef, type Ref } from 'vue'
import type { OrderCardDTO } from '@/api/orders'
import { orderPreferredTimesList } from '@/api/careOrderLineDisplay'
import { formatOrderScheduleLineDisplay } from '@/utils/orderScheduleDisplay'
import type { RescheduleEntrySnapshot } from '../types'

export interface UseCareScheduleRescheduleParams {
  isRescheduleMode: ComputedRef<boolean>
  isCustomerRequestedReschedule: ComputedRef<boolean>
  rescheduleEntrySnapshot: Ref<RescheduleEntrySnapshot | null>
  findOrder: (orderId: string) => OrderCardDTO | undefined
  originalScheduledPartsForOrder: (orderId: string) => { time: string; inspector: string }
  applyBookedOrderContext: (orderId: string) => void
  navigateCalendarToDate: (d: Date) => void
  rescheduleAnchorDateForOrder: (orderId: string) => Date
  clearSelectedSlots: () => void
  rebuildInspectorAssignmentRows: () => void
  rescheduleUserPickedSlots: Ref<boolean>
  selectedOrderId: Ref<string>
}

export function useCareScheduleReschedule(params: UseCareScheduleRescheduleParams) {
  const rescheduleEntrySnapshot = params.rescheduleEntrySnapshot

  function captureRescheduleEntrySnapshot(orderId: string, opts?: { force?: boolean }) {
    if (!orderId || !params.isRescheduleMode.value || !params.isCustomerRequestedReschedule.value) {
      return
    }
    if (!opts?.force && rescheduleEntrySnapshot.value?.orderId === orderId) return
    const { time, inspector } = params.originalScheduledPartsForOrder(orderId)
    const o = params.findOrder(orderId)
    const preferredTimes = o
      ? orderPreferredTimesList(o).map((pt) => formatOrderScheduleLineDisplay(pt))
      : []
    rescheduleEntrySnapshot.value = {
      orderId,
      originalTime: time,
      originalInspector: inspector,
      preferredTimes,
    }
  }

  function clearRescheduleEntrySnapshot() {
    rescheduleEntrySnapshot.value = null
  }

  function applyRescheduleOrderContext(orderId: string) {
    params.applyBookedOrderContext(orderId)
  }

  function applyRescheduleCalendarAnchor() {
    const orderId = params.selectedOrderId.value
    if (!orderId) return
    params.navigateCalendarToDate(params.rescheduleAnchorDateForOrder(orderId))
    params.clearSelectedSlots()
    params.rescheduleUserPickedSlots.value = false
    params.rebuildInspectorAssignmentRows()
    captureRescheduleEntrySnapshot(orderId)
  }

  return {
    captureRescheduleEntrySnapshot,
    clearRescheduleEntrySnapshot,
    applyRescheduleOrderContext,
    applyRescheduleCalendarAnchor,
  }
}
